import { useState } from 'react';
import { History } from 'lucide-react';
import { useT } from '../i18n';
import { formatMilestoneDueDate } from './ProjectForm';
import type { DateChange } from '../types';

// Formats an ISO timestamp as e.g. "18 août 2026 à 14:32", matching the
// date+time pattern used elsewhere for activity timestamps (ProjectTimeline).
function formatChangedAt(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

// A planned slot (ISO timestamp) as "dd.mm.yy HH:MM" in local time — the time
// is dropped when it's midnight, i.e. a date-only plan from the Tasks page.
export function formatPlannedSlot(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = formatMilestoneDueDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
  return d.getHours() || d.getMinutes() ? `${date} ${pad(d.getHours())}:${pad(d.getMinutes())}` : date;
}

// Toggle + list of a date field's change history, oldest-first in storage and
// shown newest-first. Used for milestones' estimated start/end and tasks' due
// and planned dates. `formatValue` renders old/new values (default: 'YYYY-MM-DD');
// `label` names the field in the toggle when the list isn't right under its input.
export function DateHistory({ history, formatValue = formatMilestoneDueDate, label }: {
  history: DateChange[] | null | undefined;
  formatValue?: (value: string) => string;
  label?: string;
}) {
  const { t } = useT();
  const [show, setShow] = useState(false);
  if (!history || history.length === 0) return null;
  return (
    <div className="ms-due-history">
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShow((v) => !v)}>
        <History size={12} /> {label && `${label} · `}{t('milestones.dueDateHistoryToggle', { n: history.length })}
      </button>
      {show && (
        <ul className="ms-due-history-list">
          {[...history].reverse().map((h, i) => (
            <li key={i}>
              {t('milestones.dueDateHistoryEntry', {
                old: h.old_date ? formatValue(h.old_date) : t('milestones.dueDateHistoryNone'),
                new: h.new_date ? formatValue(h.new_date) : t('milestones.dueDateHistoryNone'),
                date: formatChangedAt(h.changed_at),
                who: h.changed_by,
              })}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
